import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tables, Players, Venue } from "@/lib/api";
import { fmtBRL, elapsedFrom } from "@/lib/format";
import { toast } from "sonner";
import { Spade } from "@phosphor-icons/react";
import SeatMap from "@/pages/dealer/SeatMap";
import DealerSidebar from "@/pages/dealer/DealerSidebar";
import BuyInDialog from "@/pages/dealer/BuyInDialog";
import CashOutDialog from "@/pages/dealer/CashOutDialog";
import HandDialog from "@/pages/dealer/HandDialog";

const Stat = ({ label, value, testId, tone = "" }) => (
    <div data-testid={testId} className="card-lux rounded-lg p-5">
        <div className="label-mini mb-1.5">{label}</div>
        <div className={`metric-num text-3xl ${tone}`}>{value}</div>
    </div>
);

const EmptyState = () => (
    <div className="border border-dashed border-border rounded-md p-12 text-center">
        <Spade weight="fill" size={40} className="text-gold mx-auto mb-4" />
        <h3 className="font-serif text-2xl mb-2">No active tables</h3>
        <p className="text-muted-foreground mb-6">Open a table in Reception to get started.</p>
        <Link to="/reception">
            <Button className="btn-gold h-11">Go to Reception</Button>
        </Link>
    </div>
);

export default function Dealer() {
    const { tableId: paramId } = useParams();
    const [tables, setTables] = useState([]);
    const [selectedId, setSelectedId] = useState(paramId || "");
    const [data, setData] = useState(null);
    const [players, setPlayers] = useState([]);
    const [venue, setVenue] = useState({ zelle: "", venmo: "" });

    const loadTables = useCallback(async () => {
        const list = await Tables.list("active");
        setTables(list);
        setSelectedId((cur) => cur || (list[0]?.id ?? ""));
    }, []);

    const load = useCallback(async () => {
        const pl = await Players.list();
        setPlayers(pl);
        if (!selectedId) { setData(null); return; }
        try {
            const d = await Tables.get(selectedId);
            setData(d);
        } catch {
            setData(null);
        }
    }, [selectedId]);

    useEffect(() => {
        loadTables();
        Venue.payment().then(setVenue).catch(() => {});
    }, [loadTables]);

    useEffect(() => {
        load();
        const timer = setInterval(load, 8000);
        return () => clearInterval(timer);
    }, [load]);

    const seat = async (seatNumber, playerId) => {
        try {
            await Tables.seat(selectedId, { seat_number: seatNumber, player_id: playerId });
            toast.success("Player seated");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const unseat = async (seatNumber) => {
        try {
            const r = await Tables.unseat(selectedId, seatNumber);
            if (r.notified) {
                const status = r.sms?.status;
                if (status === "mock") toast.info(`Seat freed. Simulated SMS sent to ${r.notified.player_name}`);
                else if (status === "failed") toast.warning(`Seat freed. SMS failed: ${r.sms.error}`);
                else toast.success(`Seat freed. SMS sent to ${r.notified.player_name}`);
            } else {
                toast.success("Seat freed");
            }
            load();
        } catch (e) { toast.error("Failed to free seat"); }
    };

    const submitBuyin = async (payload) => {
        await Tables.addBuyin(selectedId, payload);
        toast.success(`Buy-in recorded via ${payload.method.toUpperCase()}`);
        load();
    };

    const submitCashout = async (payload) => {
        await Tables.addCashout(selectedId, payload);
        toast.success("Cash-out recorded");
        load();
    };

    const submitHand = async (payload) => {
        const r = await Tables.addHand(selectedId, payload);
        toast.success(`Hand recorded. Rake: ${fmtBRL(r.rake)}`);
        load();
    };

    if (!tables.length) return <EmptyState />;

    const seatedPlayers = (data?.table?.seats || [])
        .filter(s => s.player_id)
        .map(s => ({ id: s.player_id, name: s.player_name }));

    return (
        <div className="space-y-8" data-testid="dealer-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h2 className="font-serif text-4xl tracking-tight">Dealer Dashboard</h2>
                    <p className="text-muted-foreground text-sm mt-1">Operational control of the table</p>
                </div>
                <Select value={selectedId} onValueChange={setSelectedId}>
                    <SelectTrigger data-testid="dealer-select-table" className="w-72 h-11"><SelectValue placeholder="Pick a table" /></SelectTrigger>
                    <SelectContent>
                        {tables.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.game_type}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {data && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        <Stat testId="stat-seated" label="Seated" value={`${data.stats.seated_count}/${data.table.max_seats}`} />
                        <Stat testId="stat-hands" label="Hands" value={data.stats.hands_played} />
                        <Stat testId="stat-buyins" label="Total buy-in" value={fmtBRL(data.stats.total_buyins)} tone="text-gold" />
                        <Stat testId="stat-cashouts" label="Total cash-out" value={fmtBRL(data.stats.total_cashouts)} tone="text-gold" />
                        <Stat testId="stat-rake" label="Accumulated rake" value={fmtBRL(data.stats.total_rake)} tone="text-gold" />
                        <Stat testId="stat-time" label="Open for" value={elapsedFrom(data.table.created_at)} />
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                        <div className="xl:col-span-8 space-y-6">
                            <SeatMap table={data.table} players={players} onSeat={seat} onUnseat={unseat} />

                            <Card className="card-lux">
                                <CardContent className="p-5 flex flex-wrap gap-3 items-center">
                                    <span className="label-mini mr-2">Quick actions</span>
                                    <BuyInDialog seatedPlayers={seatedPlayers} venue={venue} onSubmit={submitBuyin} />
                                    <CashOutDialog tablePlayers={data.players} onSubmit={submitCashout} />
                                    <HandDialog rakePercent={data.table.rake_percent} rakeCap={data.table.rake_cap} onSubmit={submitHand} />
                                </CardContent>
                            </Card>
                        </div>

                        <div className="xl:col-span-4">
                            <DealerSidebar data={data} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
